import {
    DEFAULT_LOCALE,
    SUPPORTED_LOCALES,
    SupportedLocaleCode,
} from '@constants/locales';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

//~ Automatically generated types and namespaces
import { RecursivePartial } from '@types';
import { i18nNamespaces } from '@constants/i18n-namespaces';
import type {
    I18nResources,
    NamespaceResources,
} from './types/i18next-generated.d';

const localeImports = import.meta.glob('@features/language/locales/**/*.json');

const availableLanguageCodes = SUPPORTED_LOCALES.map((locale) => locale.code);

async function loadResources(): Promise<I18nResources> {
    const importPromises = Object.entries(localeImports).map(
        async ([path, importer]) => {
            const regexMatches = path.match(/locales\/(.*?)\/(.*?)\.json/);

            if (regexMatches) {
                const localeCode = regexMatches[1] as SupportedLocaleCode;
                const namespace = regexMatches[2] as keyof NamespaceResources;

                const module = (await importer()) as {
                    default: NamespaceResources[keyof NamespaceResources];
                };

                return { localeCode, namespace, data: module.default };
            }
            return null;
        },
    );

    const loadedResources = (await Promise.all(importPromises)).filter(Boolean);

    const finalResources = loadedResources.reduce<
        RecursivePartial<I18nResources>
    >((acc, current) => {
        if (!current) return acc;

        if (!acc[current.localeCode]) {
            acc[current.localeCode] = {};
        }

        acc[current.localeCode]![current.namespace] = current.data;

        return acc;
    }, {});

    return finalResources as I18nResources;
}

loadResources()
    .then((resources) => {
        i18n.use(LanguageDetector)
            .use(initReactI18next)
            .init({
                resources,
                supportedLngs: availableLanguageCodes,
                fallbackLng: DEFAULT_LOCALE,
                defaultNS: 'common',
                ns: i18nNamespaces,
                detection: {
                    order: ['localStorage', 'navigator'],
                    caches: ['localStorage'],
                },
                interpolation: { escapeValue: false },
                react: { useSuspense: false },
            });
    })
    .catch((error) =>
        console.log('Error while loading i18n resources:', error),
    );

export default i18n;
